import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { Stripe, loadStripe } from '@stripe/stripe-js'
import { IStripeCardFormRef } from '../types/Stripe'
import { TWalletAuthorizedResult } from '../types/Order'
import { StripeController } from '../controllers/StripeController'
import { useCheckoutContext } from '../hooks/useCheckoutContext'

export interface IGatewayContext {
  buyerCountry: string
  setBuyerCountry: (country: string) => void
  useStripe: boolean
  stripePromise: Promise<Stripe | null> | null
  stripeFormRef: React.RefObject<IStripeCardFormRef>
  /** Resolve `true` quando o pedido foi submetido ao backend sem rejeicao imediata. */
  submitWalletPayment: (result: TWalletAuthorizedResult) => Promise<boolean>
}

export interface IGatewayProviderProps {
  children: ReactNode
  buyerCountry: string
  setBuyerCountry: (country: string) => void
  useStripe: boolean
  stripeFormRef: React.RefObject<IStripeCardFormRef>
  onConfigError: () => void
  onWalletAuthorized: (result: TWalletAuthorizedResult) => Promise<boolean>
}

const GatewayContext = createContext<IGatewayContext | null>(null)

export const GatewayProvider = ({
  children,
  buyerCountry,
  setBuyerCountry,
  useStripe,
  stripeFormRef,
  onConfigError,
  onWalletAuthorized,
}: IGatewayProviderProps) => {
  const { productPrice } = useCheckoutContext()
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null)

  useEffect(() => {
    if (!productPrice?.internationalSalesEnabled) return
    StripeController.getConfig()
      .then(({ publishableKey }) => {
        setStripePromise(loadStripe(publishableKey))
      })
      .catch(() => {
        onConfigError()
      })
  }, [productPrice?.internationalSalesEnabled]) // eslint-disable-line react-hooks/exhaustive-deps

  // Memoizado: sem isso o value muda de identidade a cada render do checkout (que
  // re-renderiza o tempo todo por causa dos watch/polling) e o PaymentRequest do
  // Stripe e destruido/recriado sem parar. Ver finding I5.
  const value = useMemo(
    () => ({
      buyerCountry,
      setBuyerCountry,
      useStripe,
      stripePromise,
      stripeFormRef,
      submitWalletPayment: onWalletAuthorized,
    }),
    [buyerCountry, setBuyerCountry, useStripe, stripePromise, stripeFormRef, onWalletAuthorized],
  )

  return <GatewayContext.Provider value={value}>{children}</GatewayContext.Provider>
}

export const useGatewayContext = (): IGatewayContext => {
  const ctx = useContext(GatewayContext)
  if (!ctx) throw new Error('useGatewayContext must be used within GatewayProvider')
  return ctx
}
